import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IDadosCustoPedido } from '@app/modules/correcao-custo-pedidos/model/pedido_custo.model';
import { IDetalhesPedido } from '@app/modules/historico-pedidos/model/detalhes_pedido.model';
import { IHistoricoPecaRegistro } from '@app/modules/historico-pedidos/model/historico_pecas.model';
import { IHistoricoPedido } from '@app/modules/historico-pedidos/model/historico_pedido.model';
import { IHistoricoProcesso } from '@app/modules/historico-pedidos/model/historico_processo.model';
import { Utils } from '@app/shared/helpers/utils';
import { convertTimeZoneMinus } from '@app/shared/utils';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ConfigService,
  DeepPartial,
  EntityParams,
  EntityService,
  TokenService,
  Utils as UtilsCore
} from 'viggo-sistemas-core-web';
import { IEntityPagination } from 'viggo-sistemas-core-web/modules/core/model/entity.model';
import { IPedidoEvento, IPedidoImagem, IPedidos } from '../model/pedidos.model';

@Injectable()
export class PedidoService extends EntityService<IPedidos> {
  constructor(
    protected configService: ConfigService,
    protected tokenService: TokenService
  ) {
    super({
      baseEntityEndPoint: 'pedido',
      urlBackend: configService.backendURL
    });
  }

  /**
   * Joga os pedidos para produção
   * @param pedidos
   * @returns
   */
  iniciarPedidos(pedidos: {
    multiple_selection: {
      selected_list: string[];
      unselected_list: string[];
    };
    domain_id: string;
    etapa_id: string;
  }): Observable<any> {
    const url = `${this.baseURL}/iniciar_pedidos`;
    const headers = this.getHeader();
    return this.http.post<any>(url, pedidos, {
      headers
    });
  }

  /**
   * Adciona uma imagem ao produto
   * @param pedido_id identificador do produto
   * @param imagem
   */
  public addFichaTecnica(
    pedido_id: string,
    ficha_tecnica: File
  ): Observable<any> {
    const url = `${this.baseURL}/${pedido_id}/documentos`;
    const formData = new FormData();
    formData.append('file', ficha_tecnica, ficha_tecnica.name);
    const headers = this.getHeader().delete('Content-Type');

    return this.http
      .post<{ ficha_tecnica: any }>(url, formData, {
        headers
      })
      .pipe(map(response => response.ficha_tecnica));
  }

  /**
   * Adciona uma imagem ao produto
   * @param pedido_id identificador do produto
   * @param imagem
   */
  public addImagem(pedido_id: string, imagem: File): Observable<IPedidoImagem> {
    const url = `${this.baseURL}/${pedido_id}/imagens`;
    const formData = new FormData();
    formData.append('file', imagem, imagem.name);
    const headers = this.getHeader().delete('Content-Type');

    return this.http
      .post<{ produto_imagem: IPedidoImagem }>(url, formData, {
        headers
      })
      .pipe(map(response => response.produto_imagem));
  }

  /**
   * Remove a imagem de um agendamento
   * @param pedido_id identificador do agendamento
   * @param imagemId identificador da imagem
   */
  public removerImagem(pedido_id: string, imagemId: string) {
    const url = `${this.baseURL}/${pedido_id}/imagens/${imagemId}`;

    return this.http.delete(url, { headers: this.getHeader() });
  }

  /**
   * Gera um objeto evento que será inserido dentro do array de eventos
   * @returns objeto evento
   */
  initializeData(type: string, observacao?: string, estoque?: boolean) {
    return observacao
      ? ({
          status: type,
          estoque: estoque,
          observacao: observacao,
          status_dh: new Date(),
          status_por: this.tokenService.getUser().id
        } as IPedidoEvento)
      : ({
          status: type,
          status_dh: new Date(),
          status_por: this.tokenService.getUser().id
        } as IPedidoEvento);
  }

  /**
   * Gera o evento no pedido
   */
  gerarEventoPedido(
    body: any,
    type: string,
    observacao?: string,
    estoque?: boolean
  ): Observable<any> {
    const url = `${this.baseURL}/reg_evento`;

    return this.http.put(
      url,
      {
        ...body,
        evento: {
          ...(observacao
            ? this.initializeData(type, observacao, estoque)
            : this.initializeData(type))
        }
      },
      {
        headers: this.getHeader()
      }
    );
  }

  /**
   * Pega peças disponiveis para um pedido e retorna eles paginados
   * @param pedido_id
   * @param pagination
   * @param include
   * @param where
   * @returns
   */
  pecasModelo(
    pedido_id: string,
    pagination: {
      require_pagination?: boolean;
      page_size?: number;
      page?: number;
    },
    include: string[],
    domain_id: string,
    where?: string | { field: string; value: string }
  ) {
    var url = `${this.baseURL}/${pedido_id}/pecas_disponiveis`;
    var params = new HttpParams();

    if (pagination) {
      params = params
        .append('page', pagination.page)
        .append('page_size', pagination.page_size)
        .append('require_pagination', pagination.require_pagination);
    }

    if (include) params = params.append('include', include.join(','));

    if (domain_id) params = params.append('domain_id', domain_id);

    if (where) {
      if (typeof where === 'object' && Object.keys(where).length !== 0)
        params = params.append(`${where.field}`, where.value);
      else if (typeof where === 'string')
        params = params.append('peca_id', where);
    }

    return this.http.get<any>(url, {
      params: params,
      headers: this.getHeader()
    });
  }

  /**
   * Função responsável por listar processos disponiveis por pedido
   * @param parte_pedido_id
   * @param pagination
   * @param include
   * @param domain_id
   * @param where
   * @returns
   */
  processosDisponiveis(
    pedido_id: string,
    pagination: {
      require_pagination?: boolean;
      page_size?: number;
      page?: number;
    },
    include: string[],
    domain_id: string,
    linha_producao_id: string,
    where?: string | { field: string; value: string }
  ) {
    var url = `${this.baseURL}/${pedido_id}/processos_abertos`;
    var params = new HttpParams();

    if (pagination) {
      params = params
        .append('page', pagination.page)
        .append('page_size', pagination.page_size)
        .append('require_pagination', pagination.require_pagination);
    }

    if (include) params = params.append('include', include.join(','));

    if (domain_id) params = params.append('domain_id', domain_id);

    if (linha_producao_id)
      params = params.append('linha_producao_id', linha_producao_id);

    if (where) {
      if (typeof where === 'object' && Object.keys(where).length !== 0)
        params = params.append(`${where.field}`, where.value);
      else if (typeof where === 'string')
        params = params.append('processo_id', where);
    }

    return this.http.get<any>(url, {
      params: params,
      headers: this.getHeader()
    });
  }

  /**
   * Envia o múltiplos pedidos para o próx processo
   */
  enviarParaProcesso(body): Observable<any> {
    const url = `${this.baseURL}/reg_processo`;
    return this.http.put(url, body, {
      headers: this.getHeader()
    });
  }

  /**
   * Função responsável por listar os acabamentos disponiveis por pedido
   * @param parte_pedido_id
   * @param pagination
   * @param include
   * @param domain_id
   * @param where
   * @returns
   */
  acabamentosDisponiveis(
    pedido_id: string,
    pagination: {
      require_pagination?: boolean;
      page_size?: number;
      page?: number;
    },
    include: string[],
    domain_id: string,
    where: any
  ) {
    var url = `${this.baseURL}/${pedido_id}/acabamentos_disponiveis`;
    var params = new HttpParams();

    if (pagination) {
      params = params
        .append('page', pagination.page)
        .append('page_size', pagination.page_size)
        .append('require_pagination', pagination.require_pagination);
    }

    if (include) params = params.append('include', include.join(','));

    if (domain_id) params = params.append('domain_id', domain_id);

    if (where) {
      if (typeof where === 'object' && Object.keys(where).length !== 0) {
        const keys = Object.keys(where);
        keys.forEach(k => {
          if (k === 'categorias') {
            params = params.append(
              'categoria_materia_prima.descricao',
              Array(where[k]).join(',')
            );
          } else if (k === 'categoria.classificacao') {
            params = params.append(
              'categoria_materia_prima.classificacao',
              where[k]
            );
          } else {
            params = params.append(k, where[k]);
          }
        });
      } else url += where;
    }

    return this.http.get<any>(url, {
      params: params,
      headers: this.getHeader()
    });
  }

  /**
   * Finaliza o processo dos pedidos
   * @params body - mutiple_selections e filtros
   */
  processosDisponiveisMultiplos(body: any): Observable<any> {
    const url = `${this.baseURL}/processos_abertos_multiplos`;
    return this.http.put(url, body, {
      headers: this.getHeader()
    });
  }

  /**
   * Finaliza o processo dos pedidos
   * @params body - mutiple_selections e filtros
   */
  finalizaProcesso(body: any): Observable<any> {
    const url = `${this.baseURL}/finaliza_processo`;
    return this.http.put(url, body, {
      headers: this.getHeader()
    });
  }

  /**
   * Inicia a produção de pedidos naquele processo
   */
  iniciarProducaoDosPedidos(body: any): Observable<any> {
    const url = `${this.baseURL}/inicia_processo`;
    return this.http.put(url, body, {
      headers: this.getHeader()
    });
  }

  /**
   * Pesquisa uma lista de entidades apartir de um parametro repassado
   * @param params parametros repassados
   * @param domainId identificador do domain a ser buscado
   */
  pedidosMonitoramento(
    params: string | EntityParams<IPedidos>,
    domainId?: string,
    require_pagination = false,
    withPostMethod = true
  ): Observable<any> {
    const url = this.baseURL + '/monitoramento';
    return (withPostMethod
      ? this.buildPostMonitoramento(url, params, domainId, require_pagination)
      : this.buildGetMonitoramento(url, params, domainId, require_pagination)
    ).pipe(
      map(data => {
        if (
          (typeof params === 'object' &&
            params.pagination &&
            params.pagination.require_pagination) ||
          require_pagination
        ) {
          data = data as IEntityPagination<IPedidos>;
          return {
            data: data[this.entitiesName],
            pagination: data.pagination,
            total: data.total >= 0 ? data.total : 0,
            quantidade_pecas: data.quantidade_pecas,
            quantidade_produtos: data.quantidade_produtos,
            total_partes: data.total_partes >= 0 ? data.total_partes : 0
          };
        }
        data = data as IPedidos[];
        return data[this.entitiesName];
      }),
      catchError(this.handleError)
    );
  }

  buildGetMonitoramento(
    url: string,
    params: string | EntityParams<IPedidos>,
    domainId?: string,
    require_pagination = false
  ) {
    let paramsAux: string = null;
    let paramsHttp = new HttpParams();

    if (typeof params === 'string') {
      paramsAux = `?${params}${
        require_pagination ? '&require_pagination=true' : ''
      }`;
      if (domainId) paramsAux += `&domain_id=${domainId}`;
    } else {
      if (params.where) paramsHttp = this.getParams(params.where);
      if (domainId) paramsHttp = paramsHttp.set('domain_id', domainId);
      if (require_pagination) {
        paramsHttp = paramsHttp.set('require_pagination', require_pagination);
      }
      if (params.pagination) {
        paramsHttp = paramsHttp.set('page', params.pagination.page);
        paramsHttp = paramsHttp.set('page_size', params.pagination.page_size);
        if (params.pagination.require_pagination)
          paramsHttp = paramsHttp.set(
            'require_pagination',
            params.pagination.require_pagination
          );
      }
      if (params.include && params.include.length)
        paramsHttp = paramsHttp.set('include', params.include.join(','));
      if (params.order_by && params.order_by.length)
        paramsHttp = paramsHttp.set('order_by', params.order_by.join(','));
      if (params.list_options)
        paramsHttp = paramsHttp.set('list_options', params.list_options);
      paramsAux = null;
    }

    return this.http.get<any>(url + (paramsAux || ''), {
      headers: this.getHeader(),
      params: paramsHttp
    });
  }

  buildPostMonitoramento(
    url: string,
    params: string | EntityParams<IPedidos>,
    domainId?: string,
    require_pagination = false
  ) {
    const body =
      typeof params === 'string'
        ? UtilsCore.parseHttpParamsToJson(
            params +
              `&require_pagination=${require_pagination}` +
              (domainId ? `&domain_id=${domainId}` : '')
          )
        : UtilsCore.flattenObject(params, '', false, [
            'ate_filter',
            'de_filter',
            'attributes_filter'
          ]);
    return this.http.post<any>(url, this.managementStatus(body), {
      headers: this.getHeader()
    });
  }

  managementStatus(body) {
    if (!Array.isArray(body['status']) && typeof body['status'] === 'string')
      body['status'] = body['status'].split(',');
    if (
      !Array.isArray(body['processo.status']) &&
      typeof body['processo.status'] === 'string'
    )
      body['processo.status'] = body['processo.status'].split(',');
    if (
      !Array.isArray(body['pedido_parte.status']) &&
      typeof body['pedido_parte.status'] === 'string'
    )
      body['pedido_parte.status'] = body['pedido_parte.status'].split(',');
    if (
      !Array.isArray(body['status_entrega']) &&
      typeof body['status_entrega'] === 'string'
    )
      body['status_entrega'] = body['status_entrega'].split(',');
    if (
      !Array.isArray(body['status_entrega_peca']) &&
      typeof body['status_entrega_peca'] === 'string'
    )
      body['status_entrega_peca'] = body['status_entrega_peca'].split(',');
    if (Array.isArray(body['modelo.linha_producao_id']))
      body['modelo.linha_producao_id'] = body['modelo.linha_producao_id'][0];
    if (Array.isArray(body['etapa_id'])) body['etapa_id'] = body['etapa_id'][0];
    if (Array.isArray(body['processo_id']))
      body['processo_id'] = body['processo_id'][0];
    return body;
  }

  /**
   * Transforma um objeto em um parametro http
   * funcionando atualmente para unico relacionamento
   * @param params parametos a serem transformados
   */
  override getParams(params: DeepPartial<IPedidos>): HttpParams {
    let httpParams = new HttpParams();

    const getDeepParams = (
      param: DeepPartial<IPedidos>,
      addKey: string
    ): Array<{ key: string; value: string }> => {
      if (param === null || typeof param !== 'object')
        return [{ key: addKey, value: `${param}` }];

      const keys = Object.keys(param);
      if (keys.length === 1)
        return getDeepParams(param[keys[0]], `${addKey}.${keys[0]}`);

      const deppParams = keys.map(currentKey => {
        return getDeepParams(param[currentKey], `${addKey}.${currentKey}`);
      });
      return Utils.flatDeep(deppParams);
    };

    httpParams = Object.keys(params).reduce((accParam, currentKey) => {
      const currentParam = params[currentKey];
      if (typeof currentParam !== 'object')
        accParam = accParam.set(currentKey, currentParam);
      else {
        const resp = getDeepParams(currentParam, currentKey);

        resp.forEach(({ key, value }) => {
          accParam = accParam.set(key, value);
        });
      }
      return accParam;
    }, new HttpParams());

    return httpParams;
  }

  /**
   * Responsável por gerar o PDF/XLSX do relatório por falta de estoque
   * @returns
   */
  gerarRelatorioPorFalta(
    type: string,
    params: {
      tipo: string;
      domain_id: string;
      pedido_ids: string[];
      ordernar_por: string;
    }
  ): Observable<any> {
    var url = `${this.baseURL}`;

    if (type === 'PDF') {
      url += '/relatorio_falta_pedido_pdf';
    } else {
      url += '/relatorio_falta_pedido_xlsx';
    }

    const today = convertTimeZoneMinus(new Date());
    // var dataSolicitacao = convertDateToMaskTimezone(
    //   `${today.getFullYear()}-${today.getUTCMonth() + 1}-${today.getDate()}`
    // );
    params['data_solicitacao'] = today.toISOString();

    return this.http.post(url, params, {
      headers: this.getHeader(),
      responseType: 'blob'
    });
  }

  gerarFichaTecnica(id: string): Observable<any> {
    var url = `${this.baseURL}/${id}/ficha_tecnica`;
    return this.http.get(url, {
      headers: this.getHeader(),
      responseType: 'blob'
    });
  }

  gerarFichaOperacional(id: string): Observable<any> {
    var url = `${this.baseURL}/${id}/ficha_operacional`;
    return this.http.get(url, {
      headers: this.getHeader(),
      responseType: 'blob'
    });
  }

  detalhesDoPedido(id: string): Observable<IDetalhesPedido> {
    var url = `${this.baseURL}/${id}/historico_detalhes`;
    return this.http.get<IDetalhesPedido>(url, {
      headers: this.getHeader()
    });
  }

  historicoDoPedido(id: string): Observable<IHistoricoPedido> {
    var url = `${this.baseURL}/${id}/historico`;
    return this.http.get<IHistoricoPedido>(url, {
      headers: this.getHeader()
    });
  }

  historicoDoProcesso(id: string): Observable<IHistoricoProcesso> {
    var url = `${this.baseURL}/${id}/historico_processos`;
    return this.http.get<IHistoricoProcesso>(url, {
      headers: this.getHeader()
    });
  }

  historicoDaEtapa(id: string): Observable<any> {
    var url = `${this.baseURL}/${id}/historico_etapas`;
    return this.http.get<any>(url, {
      headers: this.getHeader()
    });
  }

  historicoDaPeca(id: string): Observable<any> {
    var url = `${this.baseURL}/${id}/historico_pecas_info`;
    return this.http.get<any>(url, {
      headers: this.getHeader()
    });
  }

  historicoDaPecaOperacoes(
    id: string,
    etapa_id: string,
    peca_id: string,
    pagination: {
      page: number;
      page_size: number;
      require_pagination: boolean;
    }
  ): Observable<any> {
    var url = `${this.baseURL}/${id}/historico_pecas`;
    var paramsHttp = new HttpParams();

    paramsHttp = paramsHttp.set('etapa_id', etapa_id);
    paramsHttp = paramsHttp.set('peca_id', peca_id);
    paramsHttp = paramsHttp.set('page', pagination.page);
    paramsHttp = paramsHttp.set('page_size', pagination.page_size);
    paramsHttp = paramsHttp.set(
      'require_pagination',
      pagination.require_pagination
    );

    return this.http
      .get(url, {
        headers: this.getHeader(),
        params: paramsHttp
      })
      .pipe(
        map(data => {
          if (pagination.require_pagination) {
            return {
              data: data['registros'],
              pagination: data['pagination']
            };
          }
          data = data as IHistoricoPecaRegistro[];
          return data;
        }),
        catchError(this.handleError)
      );
  }

  historicoProcessosOperacoes(
    id: string,
    processo_id: string,
    pagination: {
      page: number;
      page_size: number;
      require_pagination: boolean;
    }
  ): Observable<any> {
    var url = `${this.baseURL}/${id}/historico_processo_registros`;
    var paramsHttp = new HttpParams();

    paramsHttp = paramsHttp.set('processo_id', processo_id);
    paramsHttp = paramsHttp.set('page', pagination.page);
    paramsHttp = paramsHttp.set('page_size', pagination.page_size);
    paramsHttp = paramsHttp.set(
      'require_pagination',
      pagination.require_pagination
    );

    return this.http
      .get(url, {
        headers: this.getHeader(),
        params: paramsHttp
      })
      .pipe(
        map(data => {
          if (pagination.require_pagination) {
            return {
              data: data['registros'],
              pagination: data['pagination']
            };
          }
          data = data as IHistoricoPecaRegistro[];
          return data;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Responsável por gerar o PDF/XLSX do relatório por pedido
   * @returns
   */
  gerarRelatorioPedidos(
    type: string,
    params: EntityParams<IPedidos>
  ): Observable<any> {
    var url = `${this.baseURL}`;
    var paramsHttp = new HttpParams();

    if (type === 'PDF') {
      url += '/relatorio_pedidos_pdf';
    } else {
      url += '/relatorio_pedidos_xlsx';
    }

    const today = convertTimeZoneMinus(new Date());
    // var dataSolicitacao = convertDateToMaskTimezone(
    //   `${today.getFullYear()}-${today.getUTCMonth() + 1}-${today.getDate()}`
    // );
    params['data_solicitacao'] = today.toISOString();
    Object.keys(params).forEach(k => {
      paramsHttp = paramsHttp.set(k, params[k]);
    });

    return this.http.get(url, {
      headers: this.getHeader(),
      responseType: 'blob',
      params: paramsHttp
    });
  }
  /**
   * Responsável por gerar o PDF/XLSX da relação de consumo de materiais por pedido
   * @returns
   */
  gerarRelatorioConsumoMaterial(
    format: string,
    params: EntityParams<IPedidos>
  ): Observable<any> {
    var url = `${this.baseURL}`;
    var paramsHttp = new HttpParams();

    if (format === 'PDF') {
      url += '/relatorio_consumo_geral_pdf';
    } else {
      url += '/relatorio_consumo_geral_xlsx';
    }

    const today = convertTimeZoneMinus(new Date());
    // var dataSolicitacao = convertDateToMaskTimezone(
    //   `${today.getFullYear()}-${today.getUTCMonth() + 1}-${today.getDate()}`
    // );
    params['data_solicitacao'] = today.toISOString();
    Object.keys(params).forEach(k => {
      paramsHttp = paramsHttp.set(k, params[k]);
    });

    return this.http.post(
      url,
      {
        ...params
      },
      {
        headers: this.getHeader(),
        responseType: 'blob'
      }
    );
  }

  /**
   * Responsável por gerar o PDF/XLSX da relação de corte por pedido ou modelo
   * @returns
   */
  gerarRelatorioPorCorte(
    format: string,
    params: EntityParams<IPedidos>
  ): Observable<any> {
    var url = `${this.baseURL}`;
    var paramsHttp = new HttpParams();

    if (format === 'PDF') {
      url += '/relatorio_corte_pdf';
    } else {
      url += '/relatorio_corte_xlsx';
    }

    const today = convertTimeZoneMinus(new Date());
    // var dataSolicitacao = convertDateToMaskTimezone(
    //   `${today.getFullYear()}-${today.getUTCMonth() + 1}-${today.getDate()}`
    // );
    params['data_solicitacao'] = today.toISOString();
    Object.keys(params).forEach(k => {
      paramsHttp = paramsHttp.set(k, params[k]);
    });

    return this.http.post(
      url,
      {
        ...params
      },
      {
        headers: this.getHeader(),
        responseType: 'blob'
      }
    );
  }

  /**
   * Responsável por gerar as etiquetas dos pedidos
   * @returns
   */
  gerarEtiqueta(params: EntityParams<IPedidos>): Observable<any> {
    var url = `${this.baseURL}/gerar_etiquetas`;
    var paramsHttp = new HttpParams();

    const today = convertTimeZoneMinus(new Date());
    params['data_solicitacao'] = today.toISOString();
    Object.keys(params).forEach(k => {
      paramsHttp = paramsHttp.set(k, params[k]);
    });

    return this.http.post(
      url,
      {
        ...params
      },
      {
        headers: this.getHeader(),
        responseType: 'blob'
      }
    );
  }

  /**
   * Busca uma entidade pelo ID
   * @param id identificador da entidade
   * @param include relacionamentos a serem carregados, use apenas o array de strings
   */
  getPedido(id: string, params?: EntityParams<IPedidos>): Observable<IPedidos> {
    const url = `${this.baseURL}/${id}`;
    let paramsHttp = new HttpParams();
    if (params.include && params.include.length)
      paramsHttp = paramsHttp.set('include', params.include.join(','));
    if (params.where.informacao_conta)
      paramsHttp = paramsHttp.set('informacao_conta', params.where.informacao_conta);

    return this.http
      .get<IPedidos>(url, { headers: this.getHeader(), params: paramsHttp })
      .pipe(
        map(data => data[this.entityName]),
        catchError(this.handleError)
      );
  }
  /**
   * Pesquisa uma lista de entidades apartir de um parametro repassado
   * @param params parametros repassados
   * @param domainId identificador do domain a ser buscado
   */
  override getEntitiesByParams(
    params: string | EntityParams<IPedidos>,
    domainId?: string,
    require_pagination = false
  ): Observable<any> {
    // T[] | IEntityPagination<T[]>
    let paramsAux: string = null;
    let paramsHttp = new HttpParams();

    if (typeof params === 'string') {
      paramsAux = `?${params}${
        require_pagination ? '&require_pagination=true' : ''
      }`;
      if (domainId) paramsAux += `&domain_id=${domainId}`;
    } else {
      if (params.where) paramsHttp = this.getParams(params.where);
      if (domainId) paramsHttp = paramsHttp.set('domain_id', domainId);
      if (require_pagination) {
        paramsHttp = paramsHttp.set('require_pagination', require_pagination);
      }
      if (params.pagination) {
        paramsHttp = paramsHttp.set('page', params.pagination.page);
        paramsHttp = paramsHttp.set('page_size', params.pagination.page_size);
        if (params.pagination.require_pagination)
          paramsHttp = paramsHttp.set(
            'require_pagination',
            params.pagination.require_pagination
          );
      }
      if (params.include && params.include.length)
        paramsHttp = paramsHttp.set('include', params.include.join(','));
      if (params.order_by && params.order_by.length)
        paramsHttp = paramsHttp.set('order_by', params.order_by.join(','));
      if (params.list_options)
        paramsHttp = paramsHttp.set('list_options', params.list_options);
      paramsAux = null;
    }

    const url = this.baseURL + (paramsAux || '');

    return this.http
      .get<any>(url, {
        headers: this.getHeader(),
        params: paramsHttp
      })
      .pipe(
        map(data => {
          if (
            (typeof params === 'object' &&
              params.pagination &&
              params.pagination.require_pagination) ||
            require_pagination
          ) {
            data = data as IEntityPagination<IPedidos>;
            return {
              data: data[this.entitiesName],
              pagination: data.pagination,
              quantidade_produtos: data.quantidade_produtos
            };
          }
          data = data as IPedidos[];
          return data[this.entitiesName];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Realiza a captura dos dados para tela de correção de custos
   * @param pedido_id
   * @returns
   */
  getDadosTelaCustos(pedido_id: string) {
    const url = `${this.baseURL}/${pedido_id}/get_correcao_custo`;
    let paramsHttp = new HttpParams();

    return this.http
      .get<{ pedido: IDadosCustoPedido }>(url, {
        headers: this.getHeader(),
        params: paramsHttp
      })
      .pipe(
        map(data => {
          return data.pedido as IDadosCustoPedido;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Realiza a captura dos dados para tela de correção de custos
   * @param body
   * @returns
   */
  realizarCorrecaoCusto(body: {
    pedido: IDadosCustoPedido;
    justificativa: string;
    user_name: string;
    user_password: string;
  }) {
    const url = `${this.baseURL}/${body.pedido.id}/corrigir_custo`;
    let paramsHttp = new HttpParams();

    return this.http.put(url, body, {
      headers: this.getHeader(),
      params: paramsHttp
    });
  }

  /**
   * Realiza a operação de perguntar ao back a clonagem de um pedido e o
   * mesmo irá retornar o corpo do pedido para clonagem e as inconsistências
   * que podem existir e que irão remover algumas informações do pedido clonado
   * @param pedido_id
   * @returns
   */
  preClonagemPedido(pedido_id: string) {
    const url = `${this.baseURL}/${pedido_id}/pre_clonagem`;
    return this.http.get(url, {
      headers: this.getHeader()
    });
  }

  /**
   * Realiza a captura dos pedidos em produção que deseja concluir de forma forçada
   * @param body
   * @returns
   */
  conclusaoForcada(body: {
    multiple_selection: {
      selected_list: string[];
      unselected_list: string[];
    };
    justificativa: string;
    user_name: string;
    user_password: string;
    domain_id: string;
  }) {
    const url = `${this.baseURL}/forcar_conclusao`;
    let paramsHttp = new HttpParams();

    return this.http.put(url, body, {
      headers: this.getHeader(),
      params: paramsHttp
    });
  }

  /**
   * Gerar relatório de status de gerenciamento das peças - formato: PDF/XLSX
   * @returns
   */
  gerarRelatorioStatusEntrega(
    type: string,
    params: {
      ['domain.id']: string;
      ['linha_producao.id']: string;
      ['vendedor.id']?: string;
      processos?: string[]; // ids dos processos
      etapas?: string[]; // ids das etapas
      order_by: string; // dias_atraso ASC, dias_atraso DESC, numero ASC, numero DESC, cliente ASC, cliente DESC
    }
  ): Observable<any> {
    var url = `${this.baseURL}`;

    if (type === 'PDF') {
      url += '/relatorio_status_gerenciamento_pdf';
    } else {
      url += '/relatorio_status_gerenciamento_xlsx';
    }

    params['data_solicitacao'] = convertTimeZoneMinus(new Date()).toISOString();

    return this.http.post(
      url,
      Utils.onlyAttributes(params, [
        'domain.id',
        'vendedor.id',
        'linha_producao.id',
        'processos',
        'etapas',
        'order_by',
        'data_solicitacao'
      ]),
      {
        headers: this.getHeader(),
        responseType: 'blob'
      }
    );
  }

  /**
   * Gerar relatório de status de gerenciamento das peças - formato: PDF/XLSX
   * @returns
   */
  preCancelamento(
    body: any
  ): Observable<{
    pedidos: { numero: number; pre_cadastro_nome_razao_social: string }[];
  }> {
    var url = `${this.baseURL}/pre_cancelamento`;

    return this.http.post<{
      pedidos: { numero: number; pre_cadastro_nome_razao_social: string }[];
    }>(
      url,
      { ...body },
      {
        headers: this.getHeader()
      }
    );
  }
}
